import * as React from "react";
import {
  Platform,
  Pressable,
  StyleSheet,
  View,
  type LayoutChangeEvent,
} from "react-native";
import Animated, {
  Extrapolation,
  interpolate,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withSpring,
  type SharedValue,
} from "react-native-reanimated";
import { BlurView } from "expo-blur";
import {
  BottomTabBarHeightCallbackContext,
  type BottomTabBarProps,
} from "@react-navigation/bottom-tabs";
import { useCSSVariable } from "uniwind";
import { usePressableScale } from "@/shared/hooks/use-pressable-scale";
import {
  TAB_BAR_INDICATOR_SIZE,
  TAB_BAR_MIN_BOTTOM_GAP,
  TAB_BAR_PILL_HEIGHT,
  TAB_BAR_SIDE_MARGIN,
} from "@/shared/hooks/use-tab-bar-height";
import { cn } from "@/shared/lib/utils";

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

export function FloatingTabBar({
  state,
  descriptors,
  navigation,
  insets,
}: BottomTabBarProps) {
  const [, primaryForegroundColor, mutedForegroundColor] = useCSSVariable([
    "--color-primary",
    "--color-primary-foreground",
    "--color-muted-foreground",
  ]) as [string, string, string];

  const bottomOffset = Math.max(insets.bottom, TAB_BAR_MIN_BOTTOM_GAP);
  const isAndroid = Platform.OS === "android";

  const [rowWidth, setRowWidth] = React.useState(0);
  const [rowHeight, setRowHeight] = React.useState(0);
  const tabCount = state.routes.length;
  const tabWidth = rowWidth / tabCount;
  const indicatorLeft = (tabWidth - TAB_BAR_INDICATOR_SIZE) / 2;
  const indicatorTop =
    (rowHeight > 0 ? rowHeight : TAB_BAR_PILL_HEIGHT) / 2 -
    TAB_BAR_INDICATOR_SIZE / 2;

  const progress = useSharedValue(state.index);
  const didMeasure = React.useRef(false);

  React.useEffect(() => {
    progress.set(
      withSpring(state.index, {
        damping: 18,
        stiffness: 220,
        mass: 0.9,
      })
    );
  }, [state.index, progress]);

  const handleRowLayout = (e: LayoutChangeEvent) => {
    const { width, height } = e.nativeEvent.layout;
    setRowWidth(width);
    setRowHeight(height);
    if (!didMeasure.current) {
      didMeasure.current = true;
      progress.set(state.index);
    }
  };

  const indicatorStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: progress.value * tabWidth + indicatorLeft }],
  }));

  const onHeightChange = React.useContext(BottomTabBarHeightCallbackContext);
  React.useEffect(() => {
    onHeightChange?.(TAB_BAR_PILL_HEIGHT + bottomOffset);
  }, [onHeightChange, bottomOffset]);

  return (
    <View
      pointerEvents="box-none"
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        bottom: bottomOffset,
        paddingHorizontal: TAB_BAR_SIDE_MARGIN,
      }}
    >
      {/* Shadow and rounded clipping must live on separate views: a shadow
          on the same view as overflow:hidden gets clipped away on iOS. */}
      <View
        style={{
          borderRadius: TAB_BAR_PILL_HEIGHT / 2,
          boxShadow: "0px 8px 18px rgba(0, 0, 0, 0.35)",
        }}
      >
        <View
          className={cn(
            "border-border/40 border",
            isAndroid ? "bg-accent/85" : "bg-accent/55"
          )}
          style={{
            height: TAB_BAR_PILL_HEIGHT,
            borderRadius: TAB_BAR_PILL_HEIGHT / 2,
            overflow: "hidden",
          }}
        >
          {!isAndroid && (
            <BlurView
              tint="dark"
              intensity={40}
              style={StyleSheet.absoluteFill}
            />
          )}
          <View
            onLayout={handleRowLayout}
            accessibilityRole="tablist"
            style={{
              flex: 1,
              flexDirection: "row",
            }}
          >
            {rowWidth > 0 && (
              <Animated.View
                className="bg-primary rounded-full"
                style={[
                  indicatorStyle,
                  {
                    position: "absolute",
                    top: indicatorTop,
                    left: 0,
                    width: TAB_BAR_INDICATOR_SIZE,
                    height: TAB_BAR_INDICATOR_SIZE,
                  },
                ]}
              />
            )}

            {state.routes.map((route, index) => {
              const { options } = descriptors[route.key];
              const isFocused = state.index === index;

              return (
                <FloatingTabBarItem
                  key={route.key}
                  index={index}
                  isFocused={isFocused}
                  progress={progress}
                  options={options}
                  routeName={route.name}
                  activeColor={primaryForegroundColor}
                  inactiveColor={mutedForegroundColor}
                  onPress={() => {
                    const event = navigation.emit({
                      type: "tabPress",
                      target: route.key,
                      canPreventDefault: true,
                    });
                    if (!isFocused && !event.defaultPrevented) {
                      navigation.navigate(route.name, route.params);
                    }
                  }}
                  onLongPress={() => {
                    navigation.emit({
                      type: "tabLongPress",
                      target: route.key,
                    });
                  }}
                />
              );
            })}
          </View>
        </View>
      </View>
    </View>
  );
}

type FloatingTabBarItemProps = {
  index: number;
  isFocused: boolean;
  progress: SharedValue<number>;
  options: BottomTabBarProps["descriptors"][string]["options"];
  routeName: string;
  activeColor: string;
  inactiveColor: string;
  onPress: () => void;
  onLongPress: () => void;
};

function FloatingTabBarItem({
  index,
  isFocused,
  progress,
  options,
  routeName,
  activeColor,
  inactiveColor,
  onPress,
  onLongPress,
}: FloatingTabBarItemProps) {
  const { onPressIn, onPressOut, animatedStyle } = usePressableScale(0.9);

  // How close this tab is to being the active one (1 = active, 0 = not).
  const activeAmount = useDerivedValue(() =>
    interpolate(
      progress.value,
      [index - 1, index, index + 1],
      [0, 1, 0],
      Extrapolation.CLAMP
    )
  );

  const activeIconStyle = useAnimatedStyle(() => ({
    opacity: activeAmount.value,
  }));

  const inactiveIconStyle = useAnimatedStyle(() => ({
    opacity: 1 - activeAmount.value,
  }));

  const label =
    options.tabBarAccessibilityLabel ??
    (typeof options.title === "string" ? options.title : routeName);

  return (
    <AnimatedPressable
      onPress={onPress}
      onLongPress={onLongPress}
      onPressIn={onPressIn}
      onPressOut={onPressOut}
      accessibilityRole="tab"
      accessibilityState={{ selected: isFocused }}
      accessibilityLabel={label}
      testID={options.tabBarButtonTestID}
      style={[
        animatedStyle,
        { flex: 1, alignItems: "center", justifyContent: "center" },
      ]}
    >
      <View
        style={{
          width: 48,
          height: 48,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Animated.View
          style={[
            activeIconStyle,
            StyleSheet.absoluteFill,
            { alignItems: "center", justifyContent: "center" },
          ]}
        >
          {options.tabBarIcon?.({
            focused: true,
            color: activeColor,
            size: 24,
          })}
        </Animated.View>
        <Animated.View
          style={[
            inactiveIconStyle,
            StyleSheet.absoluteFill,
            { alignItems: "center", justifyContent: "center" },
          ]}
        >
          {options.tabBarIcon?.({
            focused: false,
            color: inactiveColor,
            size: 24,
          })}
        </Animated.View>
      </View>
    </AnimatedPressable>
  );
}
